import { memo, type ReactNode, useEffect, useRef } from 'react';

import { Modal, ModalCloseButton, type ModalProps, ModalTitle } from '@air/primitive-modal';
import { Spinner } from '@air/primitive-spinner';
import { useToasts } from '@air/provider-toast';

import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { canManageExistingCreditPacks, canOpenCreditPackModal } from '~/utils/creditUtils';

export type CreditPackModalWrapperProps = {
  title: string;
  /** When true, renders a centered spinner in place of `children`. */
  isLoading?: boolean;
  onClose: () => void;
  children?: ReactNode;
  testId?: string;
} & Pick<ModalProps, 'dangerouslyBypassFocusLock'>;

export const CreditPackModalWrapper = memo(
  ({
    title,
    isLoading = false,
    onClose,
    children,
    testId,
    dangerouslyBypassFocusLock = false,
  }: CreditPackModalWrapperProps) => {
    const { showToast } = useToasts();
    const { data: subscription, isPending: isSubscriptionPending } = useSubscriptionContext();
    const { data: creditStats, isLoading: isCreditStatsLoading } = useCreditStats();
    const canManageExisting = canManageExistingCreditPacks(subscription);

    const hasHandledIneligibilityRef = useRef<boolean>();
    useEffect(() => {
      if (hasHandledIneligibilityRef.current) {
        return;
      }
      // Existing packs can be reduced without stats. New purchases wait for the enforcement flag.
      if (isSubscriptionPending || (!canManageExisting && isCreditStatsLoading)) {
        return;
      }
      if (!canOpenCreditPackModal(subscription, creditStats)) {
        hasHandledIneligibilityRef.current = true;
        showToast('Credit packs are not available for your plan');
        onClose();
      }
    }, [canManageExisting, creditStats, isCreditStatsLoading, isSubscriptionPending, onClose, showToast, subscription]);

    return (
      <Modal
        data-testid={testId}
        dangerouslyBypassFocusLock={dangerouslyBypassFocusLock}
        isOpen
        onDismiss={onClose}
        className="flex flex-col gap-4"
      >
        <header className="flex items-start justify-between gap-4">
          <ModalTitle>{title}</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </header>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          children
        )}
      </Modal>
    );
  },
);

CreditPackModalWrapper.displayName = 'CreditPackModalWrapper';
